import React, { useCallback, useEffect, useMemo, useState } from 'react';
import qs from 'qs';
import { useLocation, Link } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { searchVips, deleteVip } from '../services/vipService';

const PER_PAGE = 10;

function ListVip({ websiteId: websiteIdProp }) {
  const location = useLocation();
  const { website_id: websiteIdFromQuery, search = '' } = useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    [location.search]
  );
  const websiteId = websiteIdProp || websiteIdFromQuery;

  const [hits, setHits] = useState([]);
  const [page, setPage] = useState(null);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const loadPage = useCallback(
    async (pageToLoad = 1) => {
      if (!websiteId) return;
      setIsLoading(true);
      try {
        const result = await searchVips(websiteId, pageToLoad, search || '');
        setHits((prev) => (pageToLoad === 1 ? result.vips : [...prev, ...result.vips]));
        setPage(parseFloat(result.page_nbr));
        setTotal(parseFloat(result.total_elem));
      } catch (e) {
        // Optionnel: afficher une notification d'erreur
        // console.error(e);
      } finally {
        setIsLoading(false);
      }
    },
    [websiteId, search]
  );

  useEffect(() => {
    // Réinitialise la liste lorsqu'on change de site ou de recherche
    setHits([]);
    setPage(null);
    setTotal(0);
    loadPage(1);
  }, [websiteId, search, loadPage]);

  const onLoadMore = useCallback(() => {
    if (page != null) {
      loadPage(page + 1);
    }
  }, [page, loadPage]);

  const onDelete = useCallback(
    async (item) => {
      if (!websiteId || !item?._id) return;
      try {
        await deleteVip(websiteId, item._id);
        // Mise à jour locale de la liste
        setHits((prev) => prev.filter((v) => v._id !== item._id));
        setTotal((t) => Math.max(0, t - 1));
      } catch (e) {
        // Optionnel: afficher une notification d'erreur
        // console.error(e);
      }
    },
    [websiteId]
  );

  const canLoadMore =
    page !== null && !isLoading && total > 0 && page < total / PER_PAGE && total / PER_PAGE > 1;

  const embedded = !!websiteIdProp;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1">
          Liste VIPs
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Total {new Intl.NumberFormat('fr-FR').format(parseInt(total || 0, 10))}
          </Typography>
          {!embedded && websiteId && (
            <Button
              variant="contained"
              color="success"
              size="small"
              component={Link}
              to={`/super-admin/FormVip?website_id=${websiteId}`}
            >
              Ajouter
            </Button>
          )}
        </Stack>
      </Stack>

      <Box sx={{ position: 'relative' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hits.map((item) => (
              <TableRow key={item._id} hover>
                <TableCell>{item.email}</TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label="Supprimer"
                    color="error"
                    size="small"
                    onClick={() => onDelete(item)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {hits.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  <Stack spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Aucun résultat
                    </Typography>
                    {!embedded && websiteId && (
                      <Button
                        variant="contained"
                        size="small"
                        component={Link}
                        to={`/super-admin/FormVip?website_id=${websiteId}`}
                      >
                        Ajouter un VIP
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {isLoading && (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={24} />
          </Stack>
        )}
      </Box>

      <Stack alignItems="center" sx={{ mt: 2 }}>
        {canLoadMore && (
          <Button variant="outlined" onClick={onLoadMore} disabled={isLoading}>
            Plus
          </Button>
        )}
      </Stack>
    </Container>
  );
}

export default ListVip;